/**
 * @voltxampere/engine — BJT Ebers-Moll DC stamp (NPN + PNP)
 *
 * SPICE-classic injection-version Ebers-Moll per Gray & Meyer
 * *Analysis and Design of Analog Integrated Circuits* Ch. 1:
 *
 *   Vbe = V_B' - V_E'           (NPN convention; PNP flips)
 *   Vbc = V_B' - V_C'
 *   If  = Is · (exp(Vbe / (Nf · Vt)) - 1)      forward injection
 *   Ir  = Is · (exp(Vbc / (Nr · Vt)) - 1)      reverse injection
 *   Ic  = If - Ir · (1 + 1/Br)
 *   Ib  = If / Bf + Ir / Br
 *   Ie  = -(Ic + Ib)
 *
 * Sprint 8c aligned the emission-coefficient names with ngspice
 * (`Ne → Nf`, `Nc → Nr`) and flipped `Nr`'s default from `2` to
 * `1` after the Sprint 8b.1 diagnostic traced a 6% residual at the
 * PNP CE operating point to the over-damped reverse term.
 *
 * Sprint 24 B1 — full SPICE3 bjtload pattern: when any of `Re`,
 * `Rb`, `Rc` is non-zero, an internal node is allocated for that
 * terminal (B'/C'/E') and a parasitic resistor is stamped between
 * the external pin (B/C/E) and the internal node. The Ebers-Moll
 * companion then evaluates at the *internal* nodes — which is where
 * the actual semiconductor junction lives in a real BJT die. With
 * all parasitics zero (Sprint 7 default), the dynamicAugments helper
 * returns no augmentations and the stamp degenerates to the original
 * Sprint 7 behaviour bit-identically (verified against Sprint 5/7
 * frozen-value sentinels).
 *
 * The stamp declares static augmentation `[]` and dynamic
 * augmentation `["internalNode" × N]` where N ∈ {0, 1, 2, 3}
 * depending on how many of Re/Rb/Rc are non-zero.
 *
 * PNP handling: the stamp flips every junction voltage and every
 * current sign, so the same math produces the canonical PNP result.
 * Parasitic resistors are unaffected by polarity (a resistor doesn't
 * care about the active-region direction).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
import { vtAtTemperature } from "./physical-constants.js";
import { computeVcrit, pnjlim } from "./pnjlim.js";
/**
 * Hard ceiling on the exponential argument. `exp(80) ≈ 5.5e34` stays
 * finite in double precision; anything much larger is poison for the
 * next linearisation. pnjlim keeps iterates bounded once the history
 * accumulates; this clamp is the first-iteration safety net.
 */
const MAX_EXP_ARG = 80;
/**
 * Sprint 24 B1 — read parazitik resistor parameters and assemble
 * the slot list in canonical B'→C'→E' order. Only non-zero values
 * receive an internal node. The function is deterministic (same
 * params → same return value) so dynamicAugments() and the stamp
 * body always agree on slot count and ordering.
 *
 * Sprint 26 Lane 17: exported so `applyInitialConditions` can mirror
 * the IC-seeded external-pin voltages onto the parazitik internal
 * nodes (B'/C'/E') using the same canonical slot ordering — without
 * a shared source of truth the IC seeder and the stamp body would
 * disagree on which `internalNodeIndices[i]` corresponds to which
 * terminal, silently corrupting the seed.
 */
export function bjtParasiticSlots(component) {
    const Rb = getNumericParam(component, "Rb", 0);
    const Rc = getNumericParam(component, "Rc", 0);
    const Re = getNumericParam(component, "Re", 0);
    const out = [];
    let cursor = 0;
    if (Rb > 0 && Number.isFinite(Rb))
        out.push({ kind: "Rb", value: Rb, slotIndex: cursor++ });
    if (Rc > 0 && Number.isFinite(Rc))
        out.push({ kind: "Rc", value: Rc, slotIndex: cursor++ });
    if (Re > 0 && Number.isFinite(Re))
        out.push({ kind: "Re", value: Re, slotIndex: cursor++ });
    return out;
}
/**
 * Sprint 24 B1 — dynamic augmentation contract. Returns one
 * `"internalNode"` per non-zero parasitic resistor. With all three
 * parazitik values zero (Sprint 7 default) the function returns an
 * empty list and the planner allocates no extra MNA rows — the
 * stamp degenerates to the Sprint 7 path bit-identically.
 */
function bjtDynamicAugments(component) {
    const slots = bjtParasiticSlots(component);
    if (slots.length === 0)
        return [];
    return slots.map(() => "internalNode");
}
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Stamp a parasitic resistor with conductance `G = 1/R` between
 * the external pin index `ext` and the internal node `internal`.
 * Standard 2-terminal resistor MNA pattern; ground compression is
 * handled by `addY` (negative indices silently dropped).
 */
function stampParasiticResistor(ctx, ext, internal, G) {
    ctx.addY(ext, ext, G);
    ctx.addY(ext, internal, -G);
    ctx.addY(internal, ext, -G);
    ctx.addY(internal, internal, G);
}
/**
 * Shared BJT stamp body. Called by the NPN and PNP stamps with the
 * polarity sign baked in at the boundary — every internal voltage
 * and current follows the NPN convention, the caller flips where
 * needed.
 */
function bjtStampCommon(ctx, component, polarity) {
    const Is = getNumericParam(component, "Is", 1e-16);
    const Bf = getNumericParam(component, "Bf", 100);
    const Br = getNumericParam(component, "Br", 1);
    // Sprint 8c: Nf/Nr replace Ne/Nc. Defaults are ngspice-aligned
    // (Nf=1, Nr=1). Old circuits that explicitly saved Ne/Nc are
    // rewritten by the 0.2.0 → 0.3.0 migration with values
    // preserved — a `Nc: 2` stays as `Nr: 2` after migration to
    // keep user-authored physics intact.
    const Nf = getNumericParam(component, "Nf", 1);
    const Nr = getNumericParam(component, "Nr", 1);
    const bjtCtx = { stampType: "bjt", componentId: component.id };
    if (!(Is > 0) || !Number.isFinite(Is)) {
        throw new StampParameterError(`bjtDCStamp: component "${component.id}" has non-positive Is (got ${String(Is)})`, { meta: { ...bjtCtx, parameter: "Is", received: Is, expected: "> 0 and finite" } });
    }
    if (!(Bf > 0) || !(Br > 0)) {
        throw new StampParameterError(`bjtDCStamp: component "${component.id}" has non-positive Bf/Br (got Bf=${String(Bf)}, Br=${String(Br)})`, { meta: { ...bjtCtx, parameter: "Bf/Br", received: { Bf, Br }, expected: "both > 0" } });
    }
    if (!(Nf > 0) || !(Nr > 0)) {
        throw new StampParameterError(`bjtDCStamp: component "${component.id}" has non-positive Nf/Nr (got Nf=${String(Nf)}, Nr=${String(Nr)})`, { meta: { ...bjtCtx, parameter: "Nf/Nr", received: { Nf, Nr }, expected: "both > 0" } });
    }
    const B = ctx.nodeIndex(getTerminalNetId(component, "base"));
    const C = ctx.nodeIndex(getTerminalNetId(component, "collector"));
    const E = ctx.nodeIndex(getTerminalNetId(component, "emitter"));
    // Sprint 24 B1 — full SPICE3 bjtload parazitik direnç pattern.
    // Allocate internal nodes B'/C'/E' for non-zero Re/Rb/Rc, stamp
    // parasitic resistors between external and internal nodes, then
    // run Ebers-Moll on the internal node voltages. With all
    // parasitics zero the slot list is empty, the loop body never
    // executes, and Bp/Cp/Ep stay equal to the external pin indices
    // — Sprint 7 fast path preserved bit-identically.
    let Bp = B;
    let Cp = C;
    let Ep = E;
    const slots = bjtParasiticSlots(component);
    for (const slot of slots) {
        const internalIdx = ctx.internalNodeIndex(component.id, slot.slotIndex);
        const G = 1 / slot.value;
        if (slot.kind === "Rb") {
            stampParasiticResistor(ctx, B, internalIdx, G);
            Bp = internalIdx;
        }
        else if (slot.kind === "Rc") {
            stampParasiticResistor(ctx, C, internalIdx, G);
            Cp = internalIdx;
        }
        else {
            // Re
            stampParasiticResistor(ctx, E, internalIdx, G);
            Ep = internalIdx;
        }
    }
    // Polarity sign: NPN uses voltages as-is; PNP sees the inverted
    // sense so we negate the raw differentials and re-negate every
    // current contribution at the RHS write.
    const sign = polarity === "npn" ? 1 : -1;
    // Read the iterate at the *internal* nodes — that's where the
    // Ebers-Moll injection equation evaluates. With zero parasitics
    // these collapse onto the external pins.
    const vBp = nodeVoltage(ctx.xPrev, Bp);
    const vCp = nodeVoltage(ctx.xPrev, Cp);
    const vEp = nodeVoltage(ctx.xPrev, Ep);
    const vbeRaw = sign * (vBp - vEp);
    const vbcRaw = sign * (vBp - vCp);
    // Sprint 80.Z L-AYARLAR-5 — runtime temperature via stamp context.
    const vt = vtAtTemperature(ctx.temperature);
    const nVtE = Nf * vt;
    const nVtC = Nr * vt;
    const vcritE = computeVcrit(nVtE, Is);
    const vcritC = computeVcrit(nVtC, Is);
    const keyBE = `${component.id}:vbe`;
    const keyBC = `${component.id}:vbc`;
    const vbePrev = ctx.junctionHistory.get(keyBE) ?? 0;
    const vbcPrev = ctx.junctionHistory.get(keyBC) ?? 0;
    const vbe = pnjlim(vbeRaw, vbePrev, nVtE, vcritE, ctx.enableDeltaVDamping);
    const vbc = pnjlim(vbcRaw, vbcPrev, nVtC, vcritC, ctx.enableDeltaVDamping);
    ctx.junctionHistory.set(keyBE, vbe);
    ctx.junctionHistory.set(keyBC, vbc);
    const argE = Math.min(vbe / nVtE, MAX_EXP_ARG);
    const argC = Math.min(vbc / nVtC, MAX_EXP_ARG);
    const expE = Math.exp(argE);
    const expC = Math.exp(argC);
    const If = Is * (expE - 1);
    const Ir = Is * (expC - 1);
    const gmf = (Is / nVtE) * expE;
    const gmr = (Is / nVtC) * expC;
    // Ebers-Moll injection currents (NPN sign convention).
    const invBf = 1 / Bf;
    const invBr = 1 / Br;
    const Ic = If - Ir * (1 + invBr);
    const Ib = If * invBf + Ir * invBr;
    const Ie = -(Ic + Ib);
    // Partial derivatives wrt internal node voltages. Using the chain
    // rule through Vbe = sign·(VBp − VEp) and Vbc = sign·(VBp − VCp):
    //   dVbe/dVBp = sign, dVbe/dVEp = -sign, dVbe/dVCp = 0
    //   dVbc/dVBp = sign, dVbc/dVCp = -sign, dVbc/dVEp = 0
    // and the polarity sign on the currents cancels with the sign on
    // each Vbe/Vbc derivative, so the admittance block is polarity-
    // invariant when indexed in (Bp, Cp, Ep) order. Only the RHS
    // companion currents need the outer polarity flip (below).
    const gBB = gmf * invBf + gmr * invBr;
    const gBC = -gmr * invBr;
    const gBE = -gmf * invBf;
    const gCB = gmf - gmr * (1 + invBr);
    const gCC = gmr * (1 + invBr);
    const gCE = -gmf;
    const gEB = -(gBB + gCB);
    const gEC = -(gBC + gCC);
    const gEE = -(gBE + gCE);
    ctx.addY(Bp, Bp, gBB);
    ctx.addY(Bp, Cp, gBC);
    ctx.addY(Bp, Ep, gBE);
    ctx.addY(Cp, Bp, gCB);
    ctx.addY(Cp, Cp, gCC);
    ctx.addY(Cp, Ep, gCE);
    ctx.addY(Ep, Bp, gEB);
    ctx.addY(Ep, Cp, gEC);
    ctx.addY(Ep, Ep, gEE);
    // Norton-equivalent currents. The linearisation is centred on the
    // *limited* operating point (vbe, vbc), not on the raw xPrev —
    // using raw xPrev here would stamp a companion that is internally
    // inconsistent with the admittances computed above. SPICE reference
    // (Nagel 1975, `bjtload.c`) constructs a consistent V_op with the
    // emitter as the reference node:
    //
    //     V_Bp_op = V_Ep_xPrev + vbe          (satisfies V_Bp_op - V_Ep_op = vbe)
    //     V_Cp_op = V_Bp_op - vbc             (satisfies V_Bp_op - V_Cp_op = vbc)
    //     V_Ep_op = V_Ep_xPrev                (reference — unchanged)
    //
    // The NPN / PNP polarity-flip happens once at the RHS write. Note
    // that the operating point is in the *internal* node coordinates;
    // when zero parasitics are present these still equal the external
    // values by construction.
    const vBpOp = vEp + sign * vbe;
    const vCpOp = vBpOp - sign * vbc;
    const vEpOp = vEp;
    const IeqB = sign * (Ib - (gBB * vBpOp + gBC * vCpOp + gBE * vEpOp));
    const IeqC = sign * (Ic - (gCB * vBpOp + gCC * vCpOp + gCE * vEpOp));
    const IeqE = sign * (Ie - (gEB * vBpOp + gEC * vCpOp + gEE * vEpOp));
    if (Bp >= 0)
        ctx.rhs[Bp] = (ctx.rhs[Bp] ?? 0) - IeqB;
    if (Cp >= 0)
        ctx.rhs[Cp] = (ctx.rhs[Cp] ?? 0) - IeqC;
    if (Ep >= 0)
        ctx.rhs[Ep] = (ctx.rhs[Ep] ?? 0) - IeqE;
}
export const bjtNpnDCStamp = (ctx, component) => {
    bjtStampCommon(ctx, component, "npn");
};
export const bjtPnpDCStamp = (ctx, component) => {
    bjtStampCommon(ctx, component, "pnp");
};
/**
 * Sprint 8a: base / collector / emitter all carry p-n junction
 * voltages (V_BE, V_BC). A Newton step that swings V_E past V_B or
 * V_C past V_B inverts the junction bias and — via the `exp(-V/Vt)`
 * chunk of Ebers-Moll — collapses the admittance to zero. The
 * planner marks these nets junction-bearing so the Newton driver
 * clamps `|ΔV|` to `10·Vt` on each iteration. Sprint 7.1 proved
 * this is the minimum fix that closes D-PNP.
 *
 * Sprint 24 B1: declared via `defineStamp({ ... dynamicAugments })`
 * to expose the parazitik internal-node augmentation. Static
 * augments stays empty; dynamic augmentation reads Re/Rb/Rc and
 * returns one `"internalNode"` per non-zero value. The
 * `junctionTerminals` declaration still references the EXTERNAL
 * pins — Sprint 8a delta-V damping clamps those, and with realistic
 * parazitik values (≤ tens of Ω) the small voltage offset between
 * external and internal nodes is well within the `10·Vt ≈ 0.26 V`
 * clamp band.
 */
export const bjtNpnDCStampDefinition = defineStamp({
    type: "bjt-npn",
    augments: [],
    dynamicAugments: bjtDynamicAugments,
    applyDC: bjtNpnDCStamp,
    junctionTerminals: ["base", "collector", "emitter"],
});
export const bjtPnpDCStampDefinition = defineStamp({
    type: "bjt-pnp",
    augments: [],
    dynamicAugments: bjtDynamicAugments,
    applyDC: bjtPnpDCStamp,
    junctionTerminals: ["base", "collector", "emitter"],
});
//# sourceMappingURL=bjt.js.map